// Cầu nối frontend ↔ backend /teacher-group (Tổ chuyên môn — năng lực số Miền 5).
import { API_CONFIG } from '../../config/api.config'

const BASE = `${API_CONFIG.python.apiUrl}/teacher-group`
const token = () =>
  localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...(init?.headers || {}) },
  })
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text().catch(() => '')}`)
  return res.json()
}

// Tải file (docx/pdf) có kèm token → blob → lưu.
async function download(path: string, filename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${token()}` } })
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text().catch(() => '')}`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}

export interface TGroup {
  id: string; name: string; subject: string; school: string; join_code: string
  owner_id: string; memberCount: number; isLeader: boolean; created_at?: string
  plan: 'free' | 'pro'; isPro: boolean; plan_until: string; maxMembers: number
}
export interface TMember { id: string; email: string; name: string; role: string; is_owner?: boolean }
export interface TPost { id: string; kind: 'notice' | 'discussion'; title: string; body: string; author_id: string; author_name: string; created_at?: string }
export interface TResource { id: string; resource_type: 'lesson' | 'course' | 'link'; resource_id: string; url: string; title: string; note: string; shared_by: string; shared_by_name: string; created_at?: string }
export interface TSelfAssess { competency_code: string; level: number; note: string; evidence: { label: string; url: string }[] }
export interface TSummary { memberCount: number; assessed: Record<string, { avg: number; count: number; dist: Record<string, number> }> }
export type TEvidence = Record<string, { counts: Record<string, number>; suggestedLevel: number }>
export interface TObservation { id: string; observer_id: string; observer_name: string; strengths: string; difficulties: string; suggestions: string; created_at?: string }
export interface TActivity {
  id: string; group_id: string; title: string; description: string
  lesson_ref: string; lesson_title: string; demo_teacher_email: string; demo_teacher_name: string
  scheduled_at: string; phase: 'design' | 'teach' | 'observe' | 'reflect' | 'done'
  created_by: string; created_by_name: string; obsCount: number; created_at?: string
}

export const teacherGroupApi = {
  listGroups: () => req<TGroup[]>('/groups'),
  createGroup: (b: { name: string; subject?: string; school?: string }) => req<TGroup>('/groups', { method: 'POST', body: JSON.stringify(b) }),
  groupDetail: (gid: string) => req<TGroup & { members: TMember[]; isOwner: boolean }>(`/groups/${gid}`),
  join: (code: string) => req<TGroup>('/join', { method: 'POST', body: JSON.stringify({ code }) }),
  addMember: (gid: string, email: string, name?: string) => req(`/groups/${gid}/members`, { method: 'POST', body: JSON.stringify({ email, name }) }),
  removeMember: (gid: string, mid: string) => req(`/groups/${gid}/members/${mid}`, { method: 'DELETE' }),
  setMemberRole: (gid: string, mid: string, role: 'leader' | 'member') => req(`/groups/${gid}/members/${mid}/role`, { method: 'POST', body: JSON.stringify({ role }) }),
  transferLeader: (gid: string, member_id: string) => req<TGroup>(`/groups/${gid}/transfer-leader`, { method: 'POST', body: JSON.stringify({ member_id }) }),

  listPosts: (gid: string) => req<TPost[]>(`/groups/${gid}/posts`),
  addPost: (gid: string, b: { kind: string; title?: string; body: string }) => req(`/groups/${gid}/posts`, { method: 'POST', body: JSON.stringify(b) }),
  delPost: (pid: string) => req(`/posts/${pid}`, { method: 'DELETE' }),

  listResources: (gid: string) => req<TResource[]>(`/groups/${gid}/resources`),
  addResource: (gid: string, b: Partial<TResource>) => req(`/groups/${gid}/resources`, { method: 'POST', body: JSON.stringify(b) }),
  delResource: (rid: string) => req(`/resources/${rid}`, { method: 'DELETE' }),

  getSelf: () => req<{ items: TSelfAssess[] }>('/competency/self'),
  saveSelf: (b: { competency_code: string; level: number; note?: string; evidence?: { label: string; url: string }[] }) =>
    req('/competency/self', { method: 'POST', body: JSON.stringify(b) }),
  summary: (gid: string) => req<TSummary>(`/groups/${gid}/competency-summary`),
  evidence: () => req<TEvidence>('/competency/evidence'),

  // Chuyên đề NCBH (5.2)
  listActivities: (gid: string) => req<TActivity[]>(`/groups/${gid}/activities`),
  createActivity: (gid: string, b: Partial<TActivity>) => req<TActivity>(`/groups/${gid}/activities`, { method: 'POST', body: JSON.stringify(b) }),
  activityDetail: (aid: string) => req<TActivity & { observations: TObservation[] }>(`/activities/${aid}`),
  updateActivity: (aid: string, b: Partial<TActivity>) => req<TActivity>(`/activities/${aid}`, { method: 'PATCH', body: JSON.stringify(b) }),
  delActivity: (aid: string) => req(`/activities/${aid}`, { method: 'DELETE' }),
  addObservation: (aid: string, b: { strengths?: string; difficulties?: string; suggestions?: string }) =>
    req(`/activities/${aid}/observations`, { method: 'POST', body: JSON.stringify(b) }),
  delObservation: (oid: string) => req(`/observations/${oid}`, { method: 'DELETE' }),

  // Thương mại (Gói Tổ)
  requestUpgrade: (gid: string, note?: string) => req<{ ok: boolean; members: number; price: number }>(`/groups/${gid}/request-upgrade`, { method: 'POST', body: JSON.stringify({ note }) }),
  adminGrantPro: (group_id: string, b: { plan?: 'pro' | 'free'; months?: number; max_members?: number }) =>
    req<TGroup>('/admin/group-pro', { method: 'POST', body: JSON.stringify({ group_id, ...b }) }),
  checkout: (gid: string) => req<{ id: string; amount_vnd: number; seats: number; memo: string; auto: boolean; vietqr: any }>(`/groups/${gid}/checkout`, { method: 'POST', body: '{}' }),

  // Nhắc lịch + xuất tài liệu
  notifySchedule: (aid: string) => req<{ ok: boolean; sent: number }>(`/activities/${aid}/notify-schedule`, { method: 'POST', body: '{}' }),
  exportCompetency: (fmt: 'docx' | 'pdf') => download(`/competency/export?fmt=${fmt}`, `ho-so-nang-luc-so.${fmt}`),
  exportActivity: (aid: string, fmt: 'docx' | 'pdf') => download(`/activities/${aid}/export?fmt=${fmt}`, `bien-ban-shcm.${fmt}`),
}
